import { useState, useEffect } from 'react';
import { ActionItem, TasksSummary, TranscriptDetail, TaskFilters, Pagination } from './types';

interface UseTasksReturn {
  // State
  tasks: ActionItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  summary: TasksSummary | null;
  pagination: Pagination;
  filters: TaskFilters;
  isSearching: boolean;
  editingTask: ActionItem | null;
  showTranscriptModal: boolean;
  showCreateModal: boolean;
  transcriptDetail: TranscriptDetail | null;
  transcriptLoading: boolean;
  availableClients: Array<{name: string, count: number}>;

  // Actions
  setFilters: (filters: TaskFilters) => void;
  clearFilters: () => void;
  loadTasks: (isLoadMore?: boolean) => Promise<void>;
  loadAvailableClients: () => Promise<void>;
  handleLoadMore: () => void;
  handleQuickStatusUpdate: (actionItemId: string, newStatus: string) => Promise<void>;
  handleEditTask: (task: ActionItem) => void;
  handleSaveTask: (editedTask: ActionItem) => Promise<void>;
  handleCreateTask: (taskData: Partial<ActionItem>) => Promise<void>;
  handleViewTranscript: (transcriptId: string) => Promise<void>;
  handleDeleteTask: (actionItemId: string) => Promise<void>;
  setEditingTask: (task: ActionItem | null) => void;
  setShowTranscriptModal: (show: boolean) => void;
  setShowCreateModal: (show: boolean) => void;
  setTranscriptDetail: (detail: TranscriptDetail | null) => void;
}

export const useTasks = (): UseTasksReturn => {
  const [tasks, setTasks] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<TasksSummary | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });
  
  // Filter states
  const [filters, setFiltersState] = useState<TaskFilters>({
    status: 'all',
    priority: 'all',
    createdBy: 'all',
    search: '',
    client: 'all'
  });
  
  const [isSearching, setIsSearching] = useState(false);

  // Modal states
  const [editingTask, setEditingTask] = useState<ActionItem | null>(null);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [transcriptDetail, setTranscriptDetail] = useState<TranscriptDetail | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  // Available clients for filter dropdown
  const [availableClients, setAvailableClients] = useState<Array<{name: string, count: number}>>([]);

  // Handle non-search filter changes (immediate)
  useEffect(() => {
    // Only handle dropdown filters here (status, priority, createdBy, client)
    // Reset pagination when non-search filters change
    setPagination(prev => ({ ...prev, offset: 0 }));
    
    // Determine if we're searching
    const isCurrentlySearching = filters.search.trim().length > 0;
    setIsSearching(isCurrentlySearching);
    
    loadTasks(false);
  }, [filters.status, filters.priority, filters.createdBy, filters.client]);

  // Handle search filter changes (debounced)
  useEffect(() => {
    // Debounce search to prevent rapid API calls
    const timeoutId = setTimeout(() => {
      // Reset pagination when search changes
      setPagination(prev => ({ ...prev, offset: 0 }));
      
      // Determine if we're searching
      const isCurrentlySearching = filters.search.trim().length > 0;
      setIsSearching(isCurrentlySearching);
      
      // Only load tasks if we have a search term or we're clearing search
      loadTasks(false);
    }, 500); // 500ms debounce for search

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  const setFilters = (newFilters: TaskFilters) => {
    setFiltersState(newFilters);
  };

  const clearFilters = () => {
    setFiltersState({
      status: 'all',
      priority: 'all',
      createdBy: 'all',
      search: '',
      client: 'all'
    });
  };

  // Function to load available clients for filter dropdown
  const loadAvailableClients = async () => {
    try {
      console.log('🔍 loadAvailableClients: Starting...');
      // Get all tasks without filters to extract unique client names
      const response = await fetch('/api/action-items/list?limit=1000', {
        credentials: 'include'
      });
      
      console.log('🔍 loadAvailableClients: Response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Failed to load clients');
      }
      
      const data = await response.json() as { actionItems: ActionItem[] };
      console.log('🔍 loadAvailableClients: Action items data:', data);
      
      // Extract unique client names and count occurrences
      const clientCounts = new Map<string, number>();
      
      data.actionItems.forEach(item => {
        if (item.client_names && item.client_names.length > 0) {
          item.client_names.forEach(clientName => {
            clientCounts.set(clientName, (clientCounts.get(clientName) || 0) + 1);
          });
        }
      });
      
      // Convert to array and sort by count (descending) then by name
      const clients = Array.from(clientCounts.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
      
      console.log('🔍 loadAvailableClients: Available clients:', clients);
      setAvailableClients(clients);
      console.log('🔍 loadAvailableClients: Successfully set available clients');
    } catch (err) {
      console.error('🔍 loadAvailableClients: Error loading available clients:', err);
    }
  };

  // Helper function to load tasks with retry logic
  const loadTasksWithRetry = async (isLoadMore = false, retryCount = 0) => {
    const maxRetries = 2;
    
    try {
      if (!isLoadMore) {
        setLoading(true);
        // Don't clear tasks immediately to prevent visual jerking
        // Tasks will be replaced when new data arrives
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      // Always use proper pagination, even for search
      const currentOffset = isLoadMore ? pagination.offset + pagination.limit : 0;
      const currentLimit = pagination.limit; // Always use standard pagination limit
      
      const params = new URLSearchParams({
        limit: currentLimit.toString(),
        offset: currentOffset.toString(),
      });
      
      // Add search parameter
      if (filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      
      // Add filter parameters
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      
      if (filters.priority !== 'all') {
        params.append('priority', filters.priority);
      }
      
      if (filters.createdBy !== 'all') {
        params.append('createdBy', filters.createdBy);
      }
      
      if (filters.client !== 'all') {
        params.append('client', filters.client);
      }
      
      const response = await fetch(`/api/action-items/list?${params}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Tasks API error:', response.status, errorText);
        throw new Error(`Failed to load tasks: ${response.status}`);
      }
      
      const data = await response.json() as { 
        actionItems: ActionItem[], 
        summary: TasksSummary,
        pagination: {
          total: number;
          limit: number;
          offset: number;
          hasMore: boolean;
        }
      };
      
      if (isLoadMore) {
        // Append to existing tasks
        setTasks(prev => [...prev, ...data.actionItems]);
      } else {
        // Replace tasks (new search or initial load)
        setTasks(data.actionItems);
      }
      
      setPagination(data.pagination);
      setSummary(data.summary || null);
      
    } catch (err) {
      console.error('Error loading tasks:', err);
      
      // Retry logic for failed requests
      if (retryCount < maxRetries) {
        console.log(`Retrying tasks load (attempt ${retryCount + 1}/${maxRetries})`);
        // Wait a bit before retrying
        setTimeout(() => {
          loadTasksWithRetry(isLoadMore, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Progressive delay
        return;
      }
      
      setError('Failed to load tasks. Please try again.');
      
      // If this was a filter/search change, don't clear existing tasks
      // Let user keep seeing previous results while they retry
      if (isLoadMore) {
        // For load more failures, we can keep the existing tasks
        // The load more button will still be available for retry
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Main loadTasks function that calls the retry version
  const loadTasks = async (isLoadMore = false) => {
    return loadTasksWithRetry(isLoadMore, 0);
  };

  const handleLoadMore = () => {
    if (!loadingMore && pagination.hasMore) {
      loadTasks(true);
    }
  };

  // Function to refresh just the summary without affecting task list pagination
  const refreshSummary = async () => {
    try {
      const params = new URLSearchParams({
        limit: '1', // Just need summary, not tasks
        offset: '0',
      });
      
      // Add current filter parameters to get accurate summary
      if (filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.priority !== 'all') {
        params.append('priority', filters.priority);
      }
      if (filters.createdBy !== 'all') {
        params.append('createdBy', filters.createdBy);
      }
      if (filters.client !== 'all') {
        params.append('client', filters.client);
      }
      
      const response = await fetch(`/api/action-items/list?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json() as { summary: TasksSummary };
        setSummary(data.summary || null);
      }
    } catch (err) {
      console.error('Error refreshing summary:', err);
      // Don't throw error, just log it
    }
  };

  const handleQuickStatusUpdate = async (actionItemId: string, newStatus: string) => {
    try {
      const currentItem = tasks.find(item => item.id === actionItemId);
      if (!currentItem) {
        throw new Error('Task not found');
      }

      const payload = {
        id: actionItemId,
        transcriptId: currentItem.transcript_id,
        title: currentItem.title,
        description: currentItem.description,
        priority: currentItem.priority,
        status: newStatus,
        assignee: currentItem.assignee,
        category: currentItem.category,
        dueDate: currentItem.due_date,
        notes: currentItem.notes
      };

      const response = await fetch('/api/action-items/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update task status');
      }

      // Update the task in the local state
      setTasks(prev => prev.map(task => 
        task.id === actionItemId 
          ? { ...task, status: newStatus as ActionItem['status'] }
          : task
      ));

      // Only refresh summary to get updated counts, don't reload all tasks
      await refreshSummary();
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status');
    }
  };

  const handleEditTask = (task: ActionItem) => {
    setEditingTask(task);
  };

  const handleSaveTask = async (editedTask: ActionItem) => {
    try {
      const payload = {
        id: editedTask.id,
        transcriptId: editedTask.transcript_id,
        title: editedTask.title,
        description: editedTask.description,
        priority: editedTask.priority,
        status: editedTask.status,
        assignee: editedTask.assignee,
        category: editedTask.category,
        dueDate: editedTask.due_date,
        notes: editedTask.notes
      };

      const response = await fetch('/api/action-items/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save task');
      }

      setEditingTask(null);
      await loadTasks(); // Reload tasks to get updated data
    } catch (err) {
      console.error('Error saving task:', err);
      setError('Failed to save task');
    }
  };

  const handleCreateTask = async (taskData: Partial<ActionItem>) => {
    try {
      const payload = {
        transcriptId: taskData.transcript_id || '',
        title: taskData.title || '',
        description: taskData.description || '',
        priority: taskData.priority || 'medium',
        status: taskData.status || 'pending',
        assignee: taskData.assignee || null,
        category: taskData.category || '',
        dueDate: taskData.due_date || null,
        notes: taskData.notes || null
      };

      const response = await fetch('/api/action-items/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to create task');
      }

      setShowCreateModal(false);
      await loadTasks(); // Reload tasks to include the new task
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task');
    }
  };

  const handleViewTranscript = async (transcriptId: string) => {
    try {
      setTranscriptLoading(true);
      setShowTranscriptModal(true);
      setTranscriptDetail(null);

      const response = await fetch(`/api/transcripts/${transcriptId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load transcript');
      }

      const data = await response.json() as { transcript: TranscriptDetail };
      setTranscriptDetail(data.transcript);
    } catch (err) {
      console.error('Error loading transcript:', err);
      setError('Failed to load transcript details');
    } finally {
      setTranscriptLoading(false);
    }
  };

  const handleDeleteTask = async (actionItemId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const response = await fetch(`/api/action-items/delete?id=${actionItemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete task');
      }

      await loadTasks(); // Reload tasks to remove the deleted task
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  return {
    // State
    tasks,
    loading,
    loadingMore,
    error,
    summary,
    pagination,
    filters,
    isSearching,
    editingTask,
    showTranscriptModal,
    showCreateModal,
    transcriptDetail,
    transcriptLoading,
    availableClients,

    // Actions
    setFilters,
    clearFilters,
    loadTasks,
    loadAvailableClients,
    handleLoadMore,
    handleQuickStatusUpdate,
    handleEditTask,
    handleSaveTask,
    handleCreateTask,
    handleViewTranscript,
    handleDeleteTask,
    setEditingTask,
    setShowTranscriptModal,
    setShowCreateModal,
    setTranscriptDetail,
  };
};